import { Box, Button, Grid, IconButton, LinearProgress, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { deleteAllLicenseLogs, getLicenseLogs } from "src/app/services";
import { LicenseLog } from "src/app/types";
import { USER_NAME_ROLE } from "src/app/auth";
import { convertUTCDateToLocalTime, formatDateSafe, formatToUTC } from "src/app/utils/helper";
import { Download } from "@mui/icons-material";
import { downloadFile } from "src/app/utils/functions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e8e8ed",
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const LicenseLogsPageContent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [logs, setLogs] = useState<LicenseLog[]>([]);
    const [page, setPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    const handleClearLogsClick = () => {
        deleteAllLicenseLogs().then(() => {
            enqueueSnackbar({
                message: "All license logs cleared successfully.",
                variant: "success",
            });
            setLogs([]);
        }).catch((err) => {
            console.error("Error clearing license logs: ", err);
            enqueueSnackbar({
                message: "Error clearing license logs.",
                variant: "error",
            });
        });
    }

    useEffect(() => {
        setLoading(true);
        getLicenseLogs(page)
            .then((response) => {
                setLogs(response.data.data);
                setTotalLogs(response.data.total);
            })
            .catch((err) => {
                console.error("Error fetching license logs: ", err);
                enqueueSnackbar({
                    message: "Error fetching license logs.",
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    }, [page]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ my: loading ? 1 : 2 }}>{loading && <LinearProgress />}</Box>
            {!loading && sessionStorage.getItem(USER_NAME_ROLE) === "admin" && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    <Button
                        size="medium"
                        sx={{ mr: "18px" }}
                        variant="outlined"
                        onClick={handleClearLogsClick}
                    >
                        Clear Logs
                    </Button>
                </Box>
            )}
            <Grid container>
                <Grid item xs={12} lg={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 500 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">Logged At</StyledTableCell>
                                    <StyledTableCell align="center">
                                        License Number
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        Serial No
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        User
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        Action
                                    </StyledTableCell>
                                    <StyledTableCell align="center">Activation Date</StyledTableCell>
                                    <StyledTableCell align="center">Expiry Date</StyledTableCell>
                                    <StyledTableCell align="center">Download</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell align="center" colSpan={7}>
                                            {loading ? "Loading..." : "No logs."}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                                {logs.map((row, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatToUTC(row.created_at)} {!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.license_number
                                                ? row.license_number
                                                : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {row.serial_no ? row.serial_no : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {row.action ? row.action : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.activation_date ? row.activation_date : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.expiry_date ? row.expiry_date : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <IconButton
                                                onClick={() => {
                                                    const content = `
                                                        Logged At: ${formatToUTC(row.created_at)} ${!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                                        User: ${row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                                        License Number: ${row.license_number ? row.license_number : "-"}
                                                        Serial No: ${row.serial_no ? row.serial_no : "-"}
                                                        Action: ${row.action ? row.action : "-"}
                                                        Activation Date: ${row.activation_date ? row.activation_date : "-"}
                                                        Expiry Date: ${row.expiry_date ? row.expiry_date : "-"}
                                                        `.replace(/^\s+/gm, "").trim();

                                                    downloadFile(
                                                        `license-log-${row.created_at}.txt`,
                                                        content,
                                                        "text/plain"
                                                    );
                                                }}
                                                aria-label="Export txt"
                                                color="primary"
                                                title="Download File"
                                            >
                                                <Download />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalLogs / 10)}
                            shape="rounded"
                            size="large"
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LicenseLogsPageContent;